import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Help, Import } from 'shared/icons'
import homeIcon from '@iconify-icons/akar-icons/home'
import cashIcon from '@iconify-icons/bi/cash'
import balanceScale from '@iconify-icons/cil/balance-scale'
import newspaperLight from '@iconify-icons/ph/newspaper-light'
import handshakeIcon from '@iconify/icons-la/handshake-solid'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'

import { LoadingStatus, UserActions, UserRole } from 'shared/types'
import { useLandingUrls, useUserRole } from 'shared/hooks'
import userStore from 'shared/store/users'
import { NavigationItem, MaterialIcon } from './Sidebar.types'

export const useNavigation = () => {
  const { pathname } = useLocation()
  const history = useHistory()

  const handleRouteChange = (route: string) => {
    history.push(route)
  }

  const isCurrentLocation = (route: string) =>
    route.length > 1 ? pathname.startsWith(route) : pathname === route

  return { handleRouteChange, isCurrentLocation }
}

export const useMenuActions = () => {
  const { data } = useSelector(userStore.selectors.getUserActions)
  const { isAllowed, loading } = useUserRole()
  const dispatch = useDispatch()

  const actions: Pick<UserActions, 'isContractorDataIncomplete'> = {
    isContractorDataIncomplete: data.isContractorDataIncomplete,
  }

  const refreshActions = async () => {
    if (isAllowed(UserRole.User) && loading === LoadingStatus.Succeeded) {
      dispatch(userStore.actions.getUserActions())
    }
  }

  return { actions, refreshActions }
}

export const useNavigationItems = () => {
  const { t } = useTranslation()
  const { isAllowed } = useUserRole()
  const urls = useLandingUrls()

  const navigationItems: NavigationItem<any>[] = [
    {
      route: '/',
      icon: homeIcon,
      title: t('mainPage'),
      allowed: [UserRole.User, UserRole.Accountant],
    },
    {
      route: '/lawyer/lawsuits',
      icon: homeIcon,
      title: t('mainPage'),
      allowed: [UserRole.Lawyer],
    },
    {
      route: '/invoices',
      icon: cashIcon,
      title: t('invoices'),
      allowed: [UserRole.User],
    },
    {
      route: '/ksef',
      icon: Import,
      title: 'KSeF',
      allowed: [UserRole.User],
      standaloneIcon: true,
    },
    {
      route: '/contractors',
      icon: handshakeIcon,
      title: t('contractors'),
      action: 'isContractorDataIncomplete',
      allowed: [UserRole.User],
    },
    {
      route: '/lawsuits',
      icon: balanceScale,
      title: t('lawsuits'),
      allowed: [UserRole.User],
    },
    {
      route: '/subscription',
      icon: newspaperLight,
      title: t('subscription'),
      allowed: [UserRole.User],
    },
    {
      route: urls.LANDING_PAGE_SUPPORT_URL,
      standaloneIcon: true,
      icon: Help,
      title: t('help'),
      allowed: [UserRole.Lawyer],
      external: true,
    },
  ]

  const userActions: NavigationItem<MaterialIcon>[] = [
    {
      route: '/account',
      icon: PersonOutlineIcon,
      title: t('profile'),
      allowed: [UserRole.User],
    },
    {
      route: urls.LANDING_PAGE_SUPPORT_URL,
      icon: Help as MaterialIcon,
      title: t('help'),
      allowed: [UserRole.User],
      external: true,
    },
  ]

  return {
    navigationItems: navigationItems.filter(
      item => !item.allowed || isAllowed(item.allowed)
    ),
    userActions: userActions.filter(
      item => !item.allowed || isAllowed(item.allowed)
    ),
  }
}
